/**
 * @fileoverview This service handles all interactions with the Google Gemini API.
 * It provides functions for generating post suggestions, refining them, and
 * generating descriptions for images. It also includes a system-wide safety instruction.
 */
import { GoogleGenAI, type GenerateContentResponse } from "@google/genai";

// A system instruction sent with every API call to ensure the AI's responses are safe and appropriate.
const AI_SAFETY_INSTRUCTION = `You are a helpful social media assistant. Your goal is to generate positive and engaging content.
- Do not generate harmful, hateful, or negative content.
- Do not ask for or provide any personal information.
- Do not discuss the application you are running on or its hosting environment.
- Do not perform web searches or attempt to access external websites. Your responses must be based solely on the provided context and your pre-existing knowledge.
- Keep the tone friendly and professional.`;

/**
 * A generic helper function to call the Gemini API for text-based content generation.
 * @param fullPrompt - The complete prompt to send to the model.
 * @returns A promise that resolves to the generated text content.
 * @throws An error if the API call fails or returns an empty response.
 */
const extractTextFromResponse = (response: GenerateContentResponse): string | undefined => {
  const anyResponse = response as unknown as {
    text?: string | (() => string);
    outputText?: string;
    output_text?: string;
    response?: {
      text?: () => string;
      outputText?: string;
      output_text?: string;
      candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
    };
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  };

  if (typeof anyResponse.text === 'function') {
    return anyResponse.text();
  }
  if (typeof anyResponse.text === 'string') {
    return anyResponse.text;
  }
  if (anyResponse.response) {
    if (typeof anyResponse.response.text === 'function') {
      return anyResponse.response.text();
    }
    if (typeof anyResponse.response.outputText === 'string') {
      return anyResponse.response.outputText;
    }
    if (typeof anyResponse.response.output_text === 'string') {
      return anyResponse.response.output_text;
    }
    const candidates = anyResponse.response.candidates;
    if (Array.isArray(candidates)) {
      for (const candidate of candidates) {
        const parts = candidate.content?.parts;
        if (Array.isArray(parts)) {
          for (const part of parts) {
            if (typeof part.text === 'string') {
              return part.text;
            }
          }
        }
      }
    }
  }
  const topLevelCandidates = anyResponse.candidates;
  if (Array.isArray(topLevelCandidates)) {
    for (const candidate of topLevelCandidates) {
      const parts = candidate.content?.parts;
      if (Array.isArray(parts)) {
        for (const part of parts) {
          if (typeof part.text === 'string') {
            return part.text;
          }
        }
      }
    }
  }
  if (typeof anyResponse.outputText === 'string') {
    return anyResponse.outputText;
  }
  if (typeof anyResponse.output_text === 'string') {
    return anyResponse.output_text;
  }
  return undefined;
};

const callGeminiForPost = async (fullPrompt: string): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  try {
    const fullPromptWithSafety = `${AI_SAFETY_INSTRUCTION}\n\nUser Request:\n${fullPrompt}`;
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [{ text: fullPromptWithSafety }],
        },
      ],
      config: {
        temperature: 0.7,
        topP: 1,
        topK: 1,
        maxOutputTokens: 200,
      },
    });

    const text = extractTextFromResponse(response);
    if (text) {
      return text.replace(/\*\*\w+:\*\*/g, '').trim();
    }
    throw new Error("Received an empty response from the AI.");
  } catch (error) {
    console.error("Error generating/refining post:", error);
    throw new Error("Failed to process suggestion due to an API error.");
  }
};

/**
 * Generates a social media post suggestion based on a user-provided prompt.
 * @param prompt - The user's topic, keywords, or draft.
 * @returns A promise that resolves to the AI-generated post suggestion.
 */
export const generatePostSuggestion = async (prompt: string): Promise<string> => {
  // Construct a detailed prompt for the AI to generate a high-quality post.
  const fullPrompt = `Generate a captivating and concise social media post based on the following topic or draft. Include 2-3 relevant hashtags. The post should be engaging and professional.

Topic/Draft: "${prompt}"`;
  
  return callGeminiForPost(fullPrompt);
};

/**
 * Refines an existing post based on a user's instruction.
 * @param originalPost - The post content to be refined.
 * @param instruction - The user's instruction for refinement (e.g., "make it shorter").
 * @returns A promise that resolves to the refined post content.
 */
export const refinePostSuggestion = async (originalPost: string, instruction: string): Promise<string> => {
  // Construct a prompt that provides context and a clear instruction for the AI.
  const fullPrompt = `You are a social media expert. Refine the following social media post based on the provided instruction. Only return the refined post content, without any extra commentary.

Original Post:
"${originalPost}"

Instruction: "${instruction}"

Refined Post:`;
  return callGeminiForPost(fullPrompt);
};

/**
 * Generates a descriptive caption for an image using Gemini's multimodal capabilities.
 * @param base64Data - The base64-encoded image data.
 * @param mimeType - The MIME type of the image (e.g., 'image/jpeg').
 * @returns A promise that resolves to a concise description of the image.
 */
export const generateImageDescription = async (base64Data: string, mimeType: string): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  try {
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [
            {
              inlineData: {
                mimeType,
                data: base64Data,
              },
            },
            {
              text: `${AI_SAFETY_INSTRUCTION}\n\nGenerate a brief, descriptive caption for this image, suitable for alt text or searching by content. Be concise.`,
            },
          ],
        },
      ],
    });

    const text = extractTextFromResponse(response);
    if (text) {
      return text.trim();
    }
    throw new Error("Received an empty response from the AI.");
  } catch(error) {
    console.error("Error generating image description:", error);
    throw new Error("Failed to generate description for the image.");
  }
};
